import { Route, Routes } from "react-router";
import RolePermissions from "../../enums/rolePermissions";
import { RouteNames } from "../../enums";
import {
  AllAccountsOverview,
  CreateIdentityProvider,
  CreateIntegration,
  EditAccount,
  EditIdentityProvider,
} from "../../features/Accounts";
import EditMsGraph from "../../features/Accounts/Integrations/EditIntegration/EditMsGraph/EditMsGraph";
import EditGoogle from "../../features/Accounts/Integrations/EditIntegration/EditGoogle/EditGoogle";
import EditWebex from "../../features/Accounts/Integrations/EditIntegration/EditWebex/EditWebex";
import EditViva from "../../features/Accounts/Integrations/EditIntegration/EditViva/EditViva";
import { IntegrationTypes } from "features/Accounts/Integrations/types";
import EditSf from "../../features/Accounts/Integrations/EditIntegration/SuccessFactors/EditSf";
import TemplatesOverview from "../../features/People/GroupTemplate/Overview/TemplatesOverview";
import CreateGroupTemplate from "../../features/People/GroupTemplate/Create/CreateGroupTemplate";
import EditTemplate from "../../features/People/GroupTemplate/Edit/EditTemplate";
import { AccountPrecreation } from "features/Accounts/CreateAccount/AccountPrecreation/AccountPrecreation";
import EditLimitedAccount from "../../features/Accounts/EditAccount/EditLimitedAccount/EditLimitedAccount";
import EditFullAccount from "../../features/Accounts/EditAccount/EditFullAccount/EditFullAccount";
import { useEffect } from "react";
import type { AppDispatch } from "features/Application/globaltypes/redux";
import { resetFilter } from "features/Accounts/AllAccountsOverview/state/slices/accountsFiltersSlice";
import { connect, type ConnectedProps } from "react-redux";
import { catchAll } from "../../utils/routeHelpers";
import { RestrictedResource } from "components";

interface Props extends PropsFromRedux {
  isMobo: boolean;
}

const AccountsContent = ({ isMobo, resetState }: Props) => {
  useEffect(() => resetState, [resetState]);

  return (
    <div className="route-content-container">
      <Routes>
        <Route
          path="home/sso/create"
          element={
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <CreateIdentityProvider />
            </RestrictedResource>
          }
        />
        {catchAll(
          "home/sso/:id",
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsView]}>
            <EditIdentityProvider />
          </RestrictedResource>,
        )}
        {catchAll(
          `home/integrations/${IntegrationTypes.Webex}`,
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
            <EditWebex />
          </RestrictedResource>,
        )}
        {catchAll(
          `home/integrations/${IntegrationTypes.SuccessFactors}`,
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
            <EditSf />
          </RestrictedResource>,
        )}
        {catchAll(
          `home/integrations/${IntegrationTypes.Viva}`,
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
            <EditViva />
          </RestrictedResource>,
        )}
        {catchAll(
          `home/integrations/${IntegrationTypes.Google}`,
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
            <EditGoogle />
          </RestrictedResource>,
        )}
        {catchAll(
          `home/integrations/${IntegrationTypes.MsGraph}`,
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
            <EditMsGraph />
          </RestrictedResource>,
        )}
        <Route
          path="home/integrations/create"
          element={
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <CreateIntegration />
            </RestrictedResource>
          }
        />
        {catchAll(
          "home",
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsView, RolePermissions.AddonsView]}>
            <EditFullAccount key="home" />
          </RestrictedResource>,
        )}
        <Route path={RouteNames.linkedAccounts}>
          <Route index element={<AllAccountsOverview />} />
          <Route
            path="create"
            element={
              <RestrictedResource
                permissions={[RolePermissions.AccountsSettingsCreate, RolePermissions.TemplatesManage]}
              >
                <AccountPrecreation />
              </RestrictedResource>
            }
          />
          <Route
            path=":moboId/sso/create"
            element={
              <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
                <CreateIdentityProvider />
              </RestrictedResource>
            }
          />
          {catchAll(
            ":moboId/sso/:id",
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsView]}>
              <EditIdentityProvider />
            </RestrictedResource>,
          )}
          {catchAll(
            `:moboId/${RouteNames.subAccountMsGraphIntegrationUrl}`,
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <EditMsGraph />
            </RestrictedResource>,
          )}
          {catchAll(
            `:moboId/${RouteNames.subAccountGoogleIntegrationUrl}`,
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <EditGoogle />
            </RestrictedResource>,
          )}
          {catchAll(
            `:moboId/${RouteNames.subAccountVivaIntegrationUrl}`,
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <EditViva />
            </RestrictedResource>,
          )}
          {catchAll(
            `:moboId/${RouteNames.subAccountSfIntegrationUrl}`,
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <EditSf />
            </RestrictedResource>,
          )}
          {catchAll(
            `:moboId/${RouteNames.subAccountWebexIntegrationUrl}`,
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
              <EditWebex />
            </RestrictedResource>,
          )}
          <Route
            path=":moboId/integrations/create"
            element={
              <RestrictedResource permissions={[RolePermissions.AccountsSettingsManage]}>
                <CreateIntegration />
              </RestrictedResource>
            }
          />
          {catchAll(
            ":moboId",
            <RestrictedResource permissions={[RolePermissions.AccountsSettingsView, RolePermissions.AddonsView]}>
              <EditAccount key="child" />
            </RestrictedResource>,
          )}
        </Route>
        <Route path={RouteNames.templates}>
          <Route
            index
            element={
              <RestrictedResource permissions={[RolePermissions.TemplatesView]}>
                <TemplatesOverview onlyOwn title="Templates" />
              </RestrictedResource>
            }
          />
          <Route
            path="add-template"
            element={
              <RestrictedResource permissions={[RolePermissions.TemplatesManage]}>
                <CreateGroupTemplate />
              </RestrictedResource>
            }
          />
          {catchAll(
            ":id",
            <RestrictedResource permissions={[RolePermissions.TemplatesView]}>
              <EditTemplate basepath={`/${RouteNames.accounts}/${RouteNames.templates}`} />
            </RestrictedResource>,
          )}
        </Route>

        {/* Limited Account */}
        {catchAll(
          "limited/:moboId",
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsView, RolePermissions.AddonsView]}>
            <EditLimitedAccount />
          </RestrictedResource>,
        )}
        {/* View-only Account */}
        {catchAll(
          "view-only/:moboId",
          <RestrictedResource permissions={[RolePermissions.AccountsSettingsView, RolePermissions.AddonsView]}>
            <EditLimitedAccount viewOnly />
          </RestrictedResource>,
        )}
      </Routes>
    </div>
  );
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetState: () => {
    dispatch(resetFilter());
  },
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AccountsContent);
export default ConnectedComponent;
